import {
  Card,
  DashboardRow,
  DataTable,
  FilterBar,
  FilterDivider,
  PanelTitle,
  Pager,
  SelectableChip,
  StatusPill,
  type DataTableColumn,
} from '../../components';
import { formatCents, formatCount } from '../../lib/format/money';
import { useTableUrlState } from '../../lib/table/tableSearch';
import { LocalePicker } from '../locales/LocalePicker';
import { listParam, pickParam, stringParam } from '../profiles/people';
import { BUCKETS, type AgingRow, type BucketKey, type PayoutStatus } from './api';
import { ChefLedgerDialog, PayoutList, RAIL_LABEL } from './ChefLedgerDialog';
import { useChefPayouts, usePayoutsAging } from './queries';

const BUCKET_LABEL: Record<BucketKey, string> = {
  current: 'Current',
  '30': '30 days',
  '60': '60 days',
  '90': '90 days',
  '180': '180 days',
  '365': '365 days',
  '365+': '365+ days',
};
const BUCKET_TONE: Record<BucketKey, string> = {
  current: 'bg-green',
  '30': 'bg-green',
  '60': 'bg-olive',
  '90': 'bg-olive',
  '180': 'bg-danger',
  '365': 'bg-danger',
  '365+': 'bg-danger',
};
const TABS = ['chefs', 'payouts'] as const;
const PAYOUT_STATUSES: PayoutStatus[] = ['pending', 'in_flight', 'paid', 'failed'];

/** The worst bucket a chef has undrawn credit in. */
function oldestBucket(r: AgingRow): BucketKey | undefined {
  return [...BUCKETS].reverse().find((b) => (r.bucketCents[b] ?? 0) > 0);
}

const CHEF_COLUMNS: DataTableColumn<AgingRow>[] = [
  {
    id: 'chef',
    header: 'Chef',
    width: '1.6fr',
    cell: (r) => (
      <span className="min-w-0">
        <span className="block truncate font-semibold">{r.displayName || r.profileId}</span>
        <span className="block text-xs text-muted">
          {[
            r.neverPaidOut && 'never paid out',
            r.bscCandidate && 'Blue Star candidate',
            !r.inSync && 'cache out of sync',
          ]
            .filter(Boolean)
            .join(' · ') || ' '}
        </span>
      </span>
    ),
  },
  {
    id: 'recognized',
    header: 'Earned',
    width: '1fr',
    align: 'right',
    numeric: true,
    cell: (r) => formatCents(r.lifetimeRecognizedCents),
  },
  {
    id: 'paid',
    header: 'Paid to date',
    width: '1fr',
    align: 'right',
    numeric: true,
    cell: (r) => (r.paidToDateCents ? formatCents(r.paidToDateCents) : '—'),
  },
  {
    id: 'flight',
    header: 'In flight',
    width: '1fr',
    align: 'right',
    numeric: true,
    cell: (r) => (r.inFlightCents ? formatCents(r.inFlightCents) : '—'),
  },
  {
    id: 'balance',
    header: 'Balance',
    width: '1fr',
    align: 'right',
    numeric: true,
    cell: (r) => <span className="font-bold text-brown-deep">{formatCents(r.balanceCents)}</span>,
  },
  {
    id: 'age',
    header: 'Oldest unpaid',
    width: '130px',
    cell: (r) => {
      const b = oldestBucket(r);
      return b ? (
        <StatusPill
          tone={b === 'current' || b === '30' ? 'success' : b === '60' || b === '90' ? 'warning' : 'danger'}
          dot
        >
          {r.oldestUndrawnDays} days
        </StatusPill>
      ) : (
        <span className="text-muted">—</span>
      );
    },
  },
  {
    id: 'rail',
    header: 'Rail last used',
    width: '140px',
    cell: (r) => <span className="text-muted">{RAIL_LABEL[r.railLastUsed]}</span>,
  },
];

/**
 * Payouts (kit `PayoutsScreen`; §7.9a, §7.10). A chef's earnings are a
 * liability the moment an order settles; this is who is owed what, for how
 * long, and the payouts that clear it. A row opens that chef's ledger.
 */
export function PayoutsScreen() {
  const table = useTableUrlState();
  const tab = pickParam(table.filters.tab, TABS) ?? 'chefs';
  const localeIds = listParam(table.filters.localeIds);
  const bucket = pickParam(table.filters.bucket, BUCKETS);
  const chefId = stringParam(table.filters.chef);
  const q = usePayoutsAging(localeIds);
  const d = q.data;
  const rows = (d?.rows ?? []).filter((r) => !bucket || (r.bucketCents[bucket] ?? 0) > 0);
  const byId = new Map((d?.rows ?? []).map((r) => [r.profileId, r]));
  const names = (ids: string[] | null | undefined) => (ids ?? []).map((id) => byId.get(id)?.displayName || id);
  const maxBucket = Math.max(1, ...BUCKETS.map((b) => d?.bucketTotals[b] ?? 0));
  const railTotal = Math.max(
    1,
    Object.values(d?.railSplit ?? {}).reduce((a, n) => a + (n ?? 0), 0),
  );
  const openChef = chefId ? byId.get(chefId) : undefined;

  return (
    <div className="flex flex-col gap-[18px]">
      <FilterBar>
        {TABS.map((t) => (
          <SelectableChip
            key={t}
            active={tab === t}
            onClick={() => table.setParams({ tab: t === 'chefs' ? undefined : t, page: undefined })}
          >
            {t === 'chefs' ? 'Chefs' : 'All payouts'}
          </SelectableChip>
        ))}
        <FilterDivider />
        <LocalePicker value={localeIds} onChange={(ids) => table.setFilter('localeIds', ids.join(','))} />
      </FilterBar>

      {tab === 'payouts' ? (
        <AllPayouts />
      ) : (
        <>
          <DashboardRow cols="repeat(4,minmax(0,1fr))" height="auto">
            <Card surface="panel">
              <PanelTitle eyebrow="Owed to chefs">Total unpaid</PanelTitle>
              <div className="font-heading text-[30px] font-extrabold tabular-nums text-brown-deep">
                {d ? formatCents(d.totalBalanceCents, { whole: true }) : '…'}
              </div>
              {d && (
                <div className="mt-1 text-xs text-muted">
                  {formatCents(d.totalRecognizedCents, { whole: true })} earned ·{' '}
                  {formatCents(d.totalPaidCents, { whole: true })} paid
                </div>
              )}
            </Card>
            <Card surface="panel">
              <PanelTitle eyebrow="Have a balance">Never paid out</PanelTitle>
              <div className="text-[13px] text-muted">
                {names(d?.neverPaidOut).join(' · ') || 'Everyone owed has been paid at least once.'}
              </div>
            </Card>
            <Card surface="panel">
              <PanelTitle eyebrow="Blue Star">Candidates not on Stripe Connect</PanelTitle>
              <div className="text-[13px] text-muted">{names(d?.bscNotOnConnect).join(' · ') || 'None.'}</div>
            </Card>
            <Card surface="panel">
              <PanelTitle eyebrow="Rails">Rail last used</PanelTitle>
              <div className="flex flex-col gap-2.5">
                {(['dots', 'stripe_connect', 'manual', 'none'] as const).map((rail, i) => {
                  const n = d?.railSplit[rail] ?? 0;
                  return (
                    <div key={rail}>
                      <div className="mb-1 flex justify-between text-[13px]">
                        <span>{RAIL_LABEL[rail]}</span>
                        <span className="tabular-nums text-muted">{formatCount(n)}</span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-[rgba(143,68,32,.1)]">
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${(n / railTotal) * 100}%`, background: `var(--admin-chart-${i + 1})` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </DashboardRow>

          {(d?.cacheExceptions?.length ?? 0) > 0 && (
            <Card surface="panel">
              <div className="text-[13px] font-semibold text-danger">
                {d!.cacheExceptions!.length} balance{d!.cacheExceptions!.length === 1 ? '' : 's'} out of sync with the
                ledger
              </div>
              <div className="mt-1 text-[13px] text-muted">
                {d!
                  .cacheExceptions!.map(
                    (c) =>
                      `${byId.get(c.profileId)?.displayName || c.profileId}: cached ${formatCents(c.cachedCents)}, ledger ${formatCents(c.ledgerCents)}`,
                  )
                  .join(' · ')}
              </div>
            </Card>
          )}

          <Card surface="panel">
            <PanelTitle eyebrow="Aging">Unpaid chef earnings by age</PanelTitle>
            <div className="grid grid-cols-7 gap-2.5">
              {BUCKETS.map((b) => {
                const amount = d?.bucketTotals[b] ?? 0;
                const active = bucket === b;
                return (
                  <button
                    key={b}
                    type="button"
                    aria-pressed={active}
                    onClick={() => table.setFilter('bucket', active ? undefined : b)}
                    className={`rounded-[10px] border px-3 py-3 text-left font-body ${active ? 'border-brown bg-[rgba(236,109,45,.1)]' : 'border-[rgba(143,68,32,.12)] bg-cream-warm'}`}
                  >
                    <div className="text-[10px] font-bold uppercase tracking-[.14em] text-muted">{BUCKET_LABEL[b]}</div>
                    <div className="mt-0.5 font-heading text-[18px] font-extrabold tabular-nums text-brown-deep">
                      {formatCents(amount, { whole: true })}
                    </div>
                    <div className="mt-2 h-[5px] overflow-hidden rounded-full bg-[rgba(143,68,32,.1)]">
                      <div
                        className={`h-full rounded-full ${BUCKET_TONE[b]}`}
                        style={{ width: `${(amount / maxBucket) * 100}%` }}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>

          <DataTable
            label="Chefs owed"
            minWidth={1100}
            columns={CHEF_COLUMNS}
            rows={rows}
            getRowId={(r) => r.profileId}
            onRowClick={(r) => table.setParams({ chef: r.profileId })}
            loading={q.isPending}
            error={q.error}
            onRetry={() => void q.refetch()}
            emptyLabel={bucket ? 'No chef has earnings in that bucket.' : 'No chef earnings yet.'}
          />
        </>
      )}

      {chefId && (
        <ChefLedgerDialog
          profileId={chefId}
          name={openChef?.displayName || 'Chef ledger'}
          onClose={() => table.setParams({ chef: undefined })}
        />
      )}
    </div>
  );
}

function AllPayouts() {
  const table = useTableUrlState();
  const status = pickParam(table.filters.status, PAYOUT_STATUSES);
  const q = useChefPayouts({ status, pageSize: table.pageSize, offset: (table.page - 1) * table.pageSize });
  return (
    <div>
      <FilterBar>
        <SelectableChip active={!status} onClick={() => table.setFilter('status', undefined)}>
          Any status
        </SelectableChip>
        {PAYOUT_STATUSES.map((s) => (
          <SelectableChip key={s} active={status === s} onClick={() => table.setFilter('status', s)}>
            {s === 'in_flight' ? 'Sent' : s.charAt(0).toUpperCase() + s.slice(1)}
          </SelectableChip>
        ))}
      </FilterBar>
      <PayoutList rows={q.data?.items ?? []} loading={q.isPending} />
      <Pager
        page={table.page}
        pageSize={table.pageSize}
        total={q.data?.total ?? 0}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        noun="payouts"
      />
    </div>
  );
}
